import * as React from "react";
import { FormEvent, useState } from "react";
import { useRequestPasswordResetMutation } from "../../../redux/services/auth";
import AuthActionButton from "../components/auth-action-button";
import AuthFormInput from "../components/auth-form-input";
import AuthFormTitle from "../components/auth-form-title";
import AuthLink from "../components/auth-link";
import AuthSuccessMessage from "../components/auth-success-message";

export type RequestPasswordResetState =
  | { status: "idle" }
  | { status: "submitting" }
  | { status: "reset_email_sent" }
  | { status: "error"; state: "unexpected_error"; message: string };

const RequestPasswordReset: React.FC = () => {
  const [requestState, setRequestState] = useState<RequestPasswordResetState>({
    status: "idle",
  });
  const [requestPasswordReset] = useRequestPasswordResetMutation();

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (requestState.status === "submitting") return;

    const formData = new FormData(e.currentTarget);
    const email = formData.get("email") as string;

    setRequestState({ status: "submitting" });

    try {
      await requestPasswordReset({ email }).unwrap();
      setRequestState({ status: "reset_email_sent" });
    } catch (error: any) {
      setRequestState({
        status: "error",
        state: "unexpected_error",
        message:
          error?.data?.message ||
          "An unexpected error occurred. Please try again.",
      });
    }
  };

  if (requestState.status === "reset_email_sent") {
    return (
      <div className="mb-3">
        <AuthSuccessMessage
          title="Check Your Email"
          message="We've sent a password reset link to the provided email address"
          helpText="If you don't see it, please check your junk folder"
        />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-column gap-4">
      <AuthFormTitle label="Reset Your Password" />

      <AuthFormInput
        id="email"
        name="email"
        label="Email"
        type="email"
        placeholder="Enter your email address"
        required
        error={requestState.status === "error" ? requestState.message : ""}
      />

      <AuthActionButton
        label="Reset Password"
        disabled={requestState.status === "submitting"}
        loading={requestState.status === "submitting"}
      />

      <AuthLink linkText="Back to Login" to="/login" className="text-center" />
    </form>
  );
};

export default RequestPasswordReset;
